import React, { useState, useEffect } from 'react';
import { getUserById } from '../utils/nomineeApi';
import { AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import Loading from './elements/Loading';
import { fetchLicense } from '../utils/subscriptionApi';
import Button from './elements/Button';

const NomineeLoginStep03 = ({ userId, next, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accessGranted, setAccessGranted] = useState(false);

  useEffect(() => {
    async function checkAccess() {
      try {
        const data = await getUserById(userId);

        // Handle access not granted
        if (!data.success) {
          if (data.code === 'ACCESS_NOT_GRANTED') {
            setAccessGranted(false);
            setError(data.message || 'Access has not been granted yet.');
            if (data.user) {
              // Store user info even if access not granted
              if (data.user.isNomineeAccessGranted === 0) {
                setAccessGranted(false);
              }
            }
          } else {
            setError(data.message || 'Failed to check access status.');
          }
          setLoading(false);
          return;
        }

        fetchLicense().then((res) => {
          if (res.success && res.license) {
            sessionStorage.setItem('license', JSON.stringify(res.license));
          }
        }).catch(() => {
          // Ignore license fetch errors
        });

        // Check if nominee access is granted
        if (data && data.user && data.user.isNomineeAccessGranted === 1) {
          setAccessGranted(true);
          next(data.user);
          return;
        } else {
          setAccessGranted(false);
          setError('Access has not been granted yet. Please wait for the account owner to grant access or for the automated access grant process to complete.');
        }

        setLoading(false);
      } catch (err) {
        setError('Could not check access status.');
        setLoading(false);
      }
    }
    checkAccess();
  }, [userId]);

  if (loading) {
    return (
      <div className="flex flex-col items-center text-accent-main font-sans">
        <Loading /> Loading...
      </div>
    );
  }

  return (
    <div className="space-y-4 font-sans">
      {accessGranted ? (
        <div className="flex items-center p-4 text-green-700 bg-green-100 rounded-lg">
          <CheckCircle className="w-6 h-6 mr-3" />
          <div>
            <p className="font-semibold">Access Granted</p>
            <p className="text-sm">You have been granted access to this account. Redirecting...</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-primary-dark text-center flex items-center justify-center font-sans">
            <XCircle className="mr-3 text-accent-main" />
            Access Pending
          </h2>
          <div className="flex items-start p-4 text-orange-700 bg-orange-100 rounded-lg">
            <div>
              <p className="text-sm mb-2">{error}</p>
              <p className="text-sm">
                Access will be automatically granted if the account owner does not respond to activity checks. 
                This typically happens after multiple failed verification attempts over a period of time.
              </p>
            </div>
          </div>
          <div className="text-center text-sm flex flex-col gap-2 font-sans">
            <Button type="anchor" onClick={onBack}>
              Back to login
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NomineeLoginStep03;
